import type { VorgangDto } from "../../services/vorgangService";
import { deriveVorgangStatus } from "../vorgang/shared/vorgangStatus";

/**
 * Die Vorgänge eines Kontakts in Ebenen zerlegen — Grundlage des
 * Vorgänge-Reiters der Kontaktakte (PO 08.09.2026: „Wir brauchen dann auch
 * eine Reihenfolge dafür: Eigene Vorgänge und dann indirekte Vorgänge (der
 * Firma, Unternehmensverbund, Haushalt)").
 *
 * Was zu welcher Ebene gehört, entscheidet das Backend (`bezug`); hier wird nur
 * sortiert und gruppiert. Ein Vorgang OHNE `bezug` gehört dem Kontakt selbst.
 */

export type BezugsEbene = "firma" | "verbund" | "haushalt";

/** Überschrift über den Abschnitten einer Ebene. */
export const EBENEN_TITEL: Record<BezugsEbene, string> = {
    firma: "Unternehmen",
    verbund: "Unternehmensverbund",
    haushalt: "Haushalt",
};

/** Anzeigereihenfolge der indirekten Ebenen. */
const REIHENFOLGE: readonly BezugsEbene[] = ["firma", "verbund", "haushalt"];

export interface BezugsGruppe {
    ebene: BezugsEbene;
    kontaktId: number;
    /** Name des Kontakts, dem diese Vorgänge gehören. */
    kontaktName: string;
    vorgaenge: VorgangDto[];
}

export interface VorgangsEbenen {
    /** Die Vorgänge des geöffneten Kontakts selbst. */
    eigen: VorgangDto[];
    /** Je Herkunftskontakt ein Abschnitt, Ebenen in Anzeigereihenfolge. */
    gruppen: BezugsGruppe[];
}

/**
 * „Offene und Erledigte Vorgänge anzeigen, nach offenen die Erledigten"
 * (PO 02.08.2026). Innerhalb der Blöcke bleibt die Server-Reihenfolge
 * (neueste zuerst) erhalten — `sort` ist in JS stabil.
 */
const offenZuerst = (vorgaenge: VorgangDto[]): VorgangDto[] =>
    [...vorgaenge].sort(
        (a, b) =>
            Number(deriveVorgangStatus(a) === "Erledigt") -
            Number(deriveVorgangStatus(b) === "Erledigt"),
    );

export function gruppiereNachBezug(vorgaenge: VorgangDto[]): VorgangsEbenen {
    const eigen: VorgangDto[] = [];
    // Ein Vorgang gehört genau EINER Ebene an. Das Backend klammert schon, aber
    // die Ansicht darf sich nicht darauf verlassen: doppelte Zeilen wären hier
    // besonders verwirrend, weil sie in zwei verschiedenen Tabellen stünden.
    const gesehen = new Set<number>();
    const proEbene = new Map<BezugsEbene, Map<number, BezugsGruppe>>();

    for (const v of vorgaenge) {
        if (gesehen.has(v.id)) continue;
        if (!v.bezug) {
            gesehen.add(v.id);
            eigen.push(v);
        }
    }

    for (const ebene of REIHENFOLGE) {
        for (const v of vorgaenge) {
            if (v.bezug?.ebene !== ebene || gesehen.has(v.id)) continue;
            gesehen.add(v.id);
            const kontaktId = v.bezug.kontaktId;
            const gruppen = proEbene.get(ebene) ?? new Map<number, BezugsGruppe>();
            const gruppe =
                gruppen.get(kontaktId) ??
                ({
                    ebene,
                    kontaktId,
                    kontaktName: (v.bezug.kontaktName ?? "").trim() || `Kontakt ${kontaktId}`,
                    vorgaenge: [],
                } satisfies BezugsGruppe);
            gruppe.vorgaenge.push(v);
            gruppen.set(kontaktId, gruppe);
            proEbene.set(ebene, gruppen);
        }
    }

    const gruppen: BezugsGruppe[] = [];
    for (const ebene of REIHENFOLGE) {
        const je = proEbene.get(ebene);
        if (!je) continue;
        gruppen.push(
            ...Array.from(je.values())
                .sort((a, b) => a.kontaktName.localeCompare(b.kontaktName, "de"))
                .map((g) => ({ ...g, vorgaenge: offenZuerst(g.vorgaenge) })),
        );
    }

    return { eigen: offenZuerst(eigen), gruppen };
}
